import * as React from 'react';
import { useMemo, useState } from 'react';
import type {
    ClusterKind,
    DayRateSnapshot,
    ProgramRateSnapshot,
    RateSnapshotData,
    RateSnapshotListItem,
    RoomRateSnapshot,
    SnapshotDayOverride,
} from './types';
import {
    getClusterCurrentDays,
    snapshotItemDisplayName,
} from './clusterUtils';
import { DayRatePriceCell } from './DayRatePriceCell';
import { SnapshotSourceCard } from './SnapshotSourceCard';
import { RemoveDropdownButton } from './RemoveDropdownButton';

interface Props {
    snapshot: RateSnapshotListItem;
    targetCurrent: RateSnapshotData | null;
    cluster: {
        id: string;
        kind: ClusterKind;
        displayName: string;
        startDate: string;
        endDate: string;
        memberIds: string[];
    };
    days: SnapshotDayOverride[];
    onDaysChange: (next: SnapshotDayOverride[]) => void;
    onClose: () => void;
    formatCurrency: (amount: number) => string;
}

export function ClusterEditorPanel({
    snapshot, targetCurrent, cluster, days, onDaysChange, onClose, formatCurrency,
}: Props) {
    const snapshotItems: (RoomRateSnapshot | ProgramRateSnapshot)[] =
        cluster.kind === 'ROOM' ? snapshot.data.rooms : snapshot.data.programs;
    const [selectedSourceIdx, setSelectedSourceIdx] = useState<number | null>(
        snapshotItems.length > 0 ? 0 : null,
    );

    const currentDays = useMemo(
        () => getClusterCurrentDays(cluster.memberIds, cluster.kind, targetCurrent),
        [cluster.memberIds, cluster.kind, targetCurrent],
    );

    const clusterDates = useMemo(() => currentDays.map(d => d.date), [currentDays]);

    const currentByDate = useMemo(() => {
        const m = new Map<string, DayRateSnapshot>();
        for (const d of currentDays) m.set(d.date, d);
        return m;
    }, [currentDays]);

    const dayByDate = useMemo(() => {
        const m = new Map<string, SnapshotDayOverride>();
        for (const d of days) m.set(d.targetDate, d);
        return m;
    }, [days]);

    function applyFrom(date: string) {
        if (selectedSourceIdx == null) return;
        const sourceDays = snapshotItems[selectedSourceIdx]?.dayRates ?? [];
        const startIdx = clusterDates.indexOf(date);
        if (startIdx < 0) return;

        const remainingClusterDays = clusterDates.length - startIdx;
        const fillCount = Math.min(remainingClusterDays, sourceDays.length);
        if (fillCount <= 0) return;

        const next = days.filter(d => {
            const idx = clusterDates.indexOf(d.targetDate);
            return idx < 0 || idx < startIdx || idx >= startIdx + fillCount;
        });

        for (let i = 0; i < fillCount; i++) {
            next.push({
                targetDate: clusterDates[startIdx + i],
                snapshotItemIndex: selectedSourceIdx,
                snapshotDayOffset: i,
            });
        }
        next.sort((a, b) => a.targetDate.localeCompare(b.targetDate));
        onDaysChange(next);
    }

    function removeJustThisDate(date: string) {
        onDaysChange(days.filter(d => d.targetDate !== date));
    }

    function removeAndAbove(date: string) {
        const idx = clusterDates.indexOf(date);
        if (idx < 0) return;
        onDaysChange(days.filter(d => {
            const i = clusterDates.indexOf(d.targetDate);
            return i >= 0 && i > idx;
        }));
    }

    function removeAndBelow(date: string) {
        const idx = clusterDates.indexOf(date);
        if (idx < 0) return;
        onDaysChange(days.filter(d => {
            const i = clusterDates.indexOf(d.targetDate);
            return i >= 0 && i < idx;
        }));
    }

    function clearAll() {
        onDaysChange([]);
    }

    return (
        <div>
            <div className="flex items-center justify-between mb-3">
                <div>
                    <h6 className="font-semibold mb-0">Editing: {cluster.displayName}</h6>
                    <div className="text-xs text-gray-500">{cluster.startDate} — {cluster.endDate}</div>
                </div>
                <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onClose}>
                    Done
                </button>
            </div>

            {snapshotItems.length === 0 ? (
                <div className="alert alert-info py-2 text-sm">
                    Snapshot has no {cluster.kind === 'ROOM' ? 'rooms' : 'programs'} to apply.
                </div>
            ) : (
                <>
                    <div className="mb-3">
                        <label className="form-label text-xs mb-1">Snapshot source (click to select)</label>
                        <div className="flex flex-wrap gap-2">
                            {snapshotItems.map((item, idx) => (
                                <SnapshotSourceCard
                                    key={idx}
                                    item={item}
                                    kind={cluster.kind}
                                    isSelected={selectedSourceIdx === idx}
                                    onSelect={() => setSelectedSourceIdx(idx)}
                                />
                            ))}
                        </div>
                    </div>

                    <div className="flex items-center justify-between mb-2">
                        <span className="text-xs text-gray-600">
                            Click <em>Apply</em> on a date to fill from there using the selected source.
                        </span>
                        {days.length > 0 && (
                            <button type="button" className="btn btn-sm btn-link text-danger p-0" onClick={clearAll}>
                                Clear all
                            </button>
                        )}
                    </div>

                    <table className="table table-sm text-xs w-full" style={{ tableLayout: 'fixed' }}>
                        <thead>
                            <tr>
                                <th style={{ width: '90px' }}>Date</th>
                                <th style={{ width: '110px' }}>Current</th>
                                <th>Source / After</th>
                                <th className="text-right" style={{ width: '180px' }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {clusterDates.map(date => {
                                const ov = dayByDate.get(date);
                                const sourceItem = ov ? snapshotItems[ov.snapshotItemIndex] : undefined;
                                const sourceLabel = sourceItem
                                    ? `${snapshotItemDisplayName(cluster.kind, sourceItem)} (day ${ov!.snapshotDayOffset + 1})`
                                    : null;
                                const sourceDay = sourceItem ? sourceItem.dayRates[ov!.snapshotDayOffset] : null;
                                const currentDay = currentByDate.get(date) ?? null;

                                return (
                                    <tr key={date}>
                                        <td className="align-top">{date}</td>
                                        <td className="align-top font-mono">
                                            {currentDay
                                                ? <DayRatePriceCell dayRate={currentDay} formatCurrency={formatCurrency} />
                                                : <span className="text-gray-400">—</span>}
                                        </td>
                                        <td className="align-top">
                                            {ov && sourceDay && sourceLabel ? (
                                                <div>
                                                    <div className="text-xs">{sourceLabel}</div>
                                                    <div className="font-mono">
                                                        <DayRatePriceCell dayRate={sourceDay} formatCurrency={formatCurrency} />
                                                    </div>
                                                </div>
                                            ) : (
                                                <span className="text-gray-400">—</span>
                                            )}
                                        </td>
                                        <td className="text-right align-top">
                                            {ov ? (
                                                <div className="flex items-center justify-end gap-1 flex-wrap">
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-outline-secondary"
                                                        title="Apply selected source from this date"
                                                        onClick={() => applyFrom(date)}
                                                        disabled={selectedSourceIdx == null}
                                                    >
                                                        Re-apply
                                                    </button>
                                                    <RemoveDropdownButton
                                                        onRemove={() => removeJustThisDate(date)}
                                                        onRemoveAndAbove={() => removeAndAbove(date)}
                                                        onRemoveAndBelow={() => removeAndBelow(date)}
                                                    />
                                                </div>
                                            ) : (
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-outline-primary"
                                                    onClick={() => applyFrom(date)}
                                                    disabled={selectedSourceIdx == null}
                                                >
                                                    Apply
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </>
            )}
        </div>
    );
}
